class CartDrawer extends HTMLElement {
 
    constructor() {
      super();
      this.isOpen = false;
      this.activeElement = null;
      this.cartUpdateUnsubscriber = null;
  
      this.addEventListener('keyup', (e) => {
        if (e.code === 'Escape') this.close();
      });
  
      this.setHeaderCartIconAccessibility();
    }
  
    connectedCallback() {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
      }
      
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event.source === 'product-form') {
          this.open();
          this.renderContents(event.cartData || event.detail?.cartData);
        }
      });
  
      const observerTarget = this.querySelector('.drawer__inner');
      if (observerTarget) {
        this._observer = new MutationObserver(() => {
          this.setupAddToCartButtons();
        });
        this._observer.observe(observerTarget, { childList: true, subtree: true });
      }
  
      this.setupAddToCartButtons();
    }
  
    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
      if (this._observer) this._observer.disconnect();
    }
  
    setHeaderCartIconAccessibility() {
      const cartIcon = document.querySelector('#cart-icon-bubble');
      if (!cartIcon) return;
  
      cartIcon.setAttribute('role', 'button');
      cartIcon.setAttribute('aria-haspopup', 'dialog');
  
      cartIcon.addEventListener('click', (e) => {
        e.preventDefault();
        this.open(cartIcon);
      });
  
      cartIcon.addEventListener('keydown', (e) => {
        if (e.code.toUpperCase() === 'SPACE') {
          e.preventDefault();
          this.open(cartIcon);
        }
      });
    }
  
    setupAddToCartButtons() {
      this.querySelectorAll('[data-add-to-cart-button]').forEach((button) => {
        button.removeEventListener('click', this.handleAddToCart);
        button.addEventListener('click', this.handleAddToCart.bind(this));
      });

    }
  
    handleAddToCart = async (event) => {
      event.preventDefault();
      const button = event.currentTarget;
  
      try {
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
  
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: button.dataset.variantId, quantity: 1 }]
          })
        });
  
        if (!response.ok) throw new Error('Failed to add to cart');
  
        const cartRes = await fetch(`${window.Shopify.routes.root}cart.js`);
        if (!cartRes.ok) throw new Error('Failed to fetch cart');
  
        const cartData = await cartRes.json();
        await this.renderContents(cartData);
        this.open();
  
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer',
          cartData: cartData
        });
      } catch (err) {
        console.error('Add to cart error:', err);
      } finally {
        button.removeAttribute('aria-busy');
        button.disabled = false;
      }
    };
  
    async renderContents(cartData) {
      if (!cartData || typeof cartData !== 'object') return;
  
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.classList.toggle('is-empty', cartData.item_count === 0);
      }
  
      const sections = this.getSectionsToRender();
      for (const section of sections) {
        const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (!el) continue;
  
        try {
          if (section.id === 'cart-drawer') {
            const res = await fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`);
            const html = await res.text();
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const sourceEl = parsed.querySelector(section.selector || `#${section.id}`);
            if (sourceEl) el.innerHTML = sourceEl.innerHTML;
          } else if (cartData.sections?.[section.section]) {
            el.innerHTML = this.getSectionInnerHTML(cartData.sections[section.section], section.selector);
          }
        } catch (err) {
          console.error(`Error updating section ${section.id}:`, err);
        }
      }
  
    //   setTimeout(() => {
    //     this.setupAddToCartButtons();
    //     this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    //   }, 100);
    }
  
    open(triggerEl = null) {
      if (this.isOpen) return;
      this.isOpen = true;
      if (triggerEl) this.setActiveElement(triggerEl);
  
      try {
        pauseAllMedia();
      } catch (e) {}
  
      this.classList.add('animate', 'active');
      document.body.classList.add('overflow-hidden');
  
      this.addEventListener(
        'transitionend',
        () => {
          const focusEl = this.querySelector('[autofocus]') || this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(this, focusEl);
        },
        { once: true }
      );
    }
  
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
  
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
  
      removeTrapFocus(this.activeElement);
    }
  
    setActiveElement(el) {
      this.activeElement = el;
    }
  
    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.querySelector(selector)?.innerHTML || '';
    }
  
    getSectionsToRender() {
      return [
        { id: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble' }
      ];
    }
  }
  
  customElements.define('cart-drawer', CartDrawer);
  
  // CartDrawerItems extending CartItems
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner', forceFullRender: true },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
      ];
    }
  }
  customElements.define('cart-drawer-items', CartDrawerItems);
  
  // Tab toggling
  document.addEventListener('DOMContentLoaded', () => {
    document.addEventListener('click', (e) => {
      const tabButton = e.target.closest('.onglet');
      if (!tabButton) return;
  
      const tabContentId = tabButton.getAttribute('aria-controls');
      const allTabs = document.querySelectorAll('.tab-content');
      const targetTab = document.getElementById(tabContentId);
  
      if (tabButton.getAttribute('aria-selected') === 'true') {
        tabButton.setAttribute('aria-selected', 'false');
        targetTab.setAttribute('hidden', '');
      } else {
        document.querySelectorAll('.onglet').forEach((btn) => btn.setAttribute('aria-selected', 'false'));
        allTabs.forEach((tab) => tab.setAttribute('hidden', ''));
  
        tabButton.setAttribute('aria-selected', 'true');
        targetTab.removeAttribute('hidden');
      }
    });
  });
  // Global click handler for add-to-cart buttons outside <cart-drawer>
document.addEventListener('click', async (e) => {
    const button = e.target.closest('[data-add-to-cart-button]');
    if (!button) return;
  
    e.preventDefault();
  
    const variantId = button.dataset.variantId;
    if (!variantId) {
      console.warn('Missing variant ID on button');
      return;
    }
  
    try {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
  
      // Add item to cart
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }]
        })
      });
  
      if (!response.ok) throw new Error('Failed to add to cart');
  
      // Get updated cart data
      const cartRes = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!cartRes.ok) throw new Error('Failed to fetch cart');
  
      const cartData = await cartRes.json();
  
      // Open cart drawer and render new contents
      const drawer = document.querySelector('cart-drawer');
      if (drawer) {
        await drawer.renderContents(cartData);
        drawer.open();
      }
  
      // Notify other parts of the app (if needed)
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'global-button',
        cartData
      });
  
    } catch (err) {
      console.error('Error adding to cart:', err);
    } finally {
      button.removeAttribute('aria-busy');
      button.disabled = false;
    }
  });
  
  // Promo code handling
  document.addEventListener('DOMContentLoaded', () => {
    document.addEventListener('click', (e) => {
      const promoButton = e.target.closest('.promo-button');
      if (!promoButton) return;
  
      e.preventDefault();
      const promoInput = document.querySelector('#CartDrawer-Promo');
      const promoCode = promoInput?.value.trim();
  
      if (!promoCode) {
        console.warn('No promo code entered');
        return;
      }
  
      fetch(`/discount/check?code=${encodeURIComponent(promoCode)}`)
        .then((res) => {
          if (res.ok) {
            window.location.href = `/checkout?discount=${encodeURIComponent(promoCode)}`;
          } else {
            console.error('Invalid promo code');
          }
        })
        .catch(() => console.error('Error applying promo code'));
    });
  });
